import {useCallback, useEffect, useRef, useState} from 'react';

import {getAccountProductHistory} from 'src/openapi/admin/apis/GetAccountProductHistoryApi';
import {useAppDispatch} from 'src/store/hooks';

import type {HistoryGrouping} from './chart-period';
import type {AccountProductHistory} from 'src/openapi/admin/models/AccountProductHistory';
import type {Error as ApiError} from 'src/openapi/admin/models/Error';

type Props = {
    accountId: number,
    startDate: string,
    endDate: string,
    groupBy: HistoryGrouping,
    enabled: boolean,
};
type Result = {key: string, data: AccountProductHistory | null, error: ApiError | Error | null};

export const useAccountProductHistoryChartData = ({accountId, startDate, endDate, groupBy, enabled}: Props) => {
    const dispatch = useAppDispatch();
    const [attempt, setAttempt] = useState(0);
    const [result, setResult] = useState<Result | null>(null);
    const cached = useRef<Result | null>(null);
    const key = `${accountId}:${startDate}:${endDate}:${groupBy}:${attempt}`;

    useEffect(() => {
        if (!enabled || cached.current?.key === key) {
            return;
        }
        const controller = new AbortController();
        const load = async () => {
            let next: Result;
            try {
                const {payload} = await dispatch(getAccountProductHistory(
                    {accountId, startDate, endDate, groupBy}, {signal: controller.signal},
                ));
                next = {key, data: payload.error ? null : payload.data, error: payload.error ? payload.data : null};
            } catch (error) {
                next = {key, data: null, error: error instanceof Error ? error : new Error('Unable to load history')};
            }
            if (!controller.signal.aborted) {
                cached.current = next;
                setResult(next);
            }
        };
        load();
        return () => controller.abort();
    }, [dispatch, accountId, startDate, endDate, groupBy, enabled, key]);

    const loadData = useCallback(() => setAttempt(value => value + 1), []);
    const current = result?.key === key ? result : null;
    return {
        rows: current?.data?.rows ?? [],
        products: current?.data?.products ?? [],
        isLoading: enabled && !current,
        loadingError: current?.error ?? null,
        loadData,
    };
};
